<template>
	<v-dialog v-model="visible" max-width="360">
		<template v-slot:activator="{ on, attrs }">
			<slot v-bind="{ on, attrs }" />
		</template>
		<v-card :loading="loading">
			<layout-error-boundary
				ref="errBoundary"
				v-slot="{ hasError, err }"
				:on-error="handleFormError"
			>
				<v-form ref="form" lazy-validation @submit.prevent="close(true)">
					<v-card-title class="headline">{{ strings.title }}</v-card-title>
					<v-card-text>
						<v-alert
							v-if="hasError"
							type="error"
							border="left"
							dismissible
							outlined
							@click.native="$refs.errBoundary.resetError()"
						>
							<span class="error--text">{{
								`${strings.error}${err.message ? ": " + err.message : ""}`
							}}</span>
						</v-alert>

						<v-text-field
							v-model="name"
							:rules="nameRules"
							label="Name"
							type="text"
							required
						/>
					</v-card-text>
					<v-card-actions>
						<v-spacer></v-spacer>
						<v-btn color="rust-red" text @click="close(false)">Cancel</v-btn>
						<v-btn color="ocean darken-1" outlined @click="close(true)"
							>Rename</v-btn
						>
					</v-card-actions>
				</v-form>
			</layout-error-boundary>
		</v-card>
	</v-dialog>
</template>

<script>
import strings from "@/utils/strings";
import { APIError, APIAuthError } from "@/utils/http";
import { NodeOperationError } from "@/utils/k4util";

const initData = () => ({
	strings: strings.dialogs.rename,
	visible: false,
	loading: false,

	node: null,
	resolve: null,
	handleSubmit: null,

	name: null,
	nameRules: [v => !!v || "Name is required", v => !v || !v.match(/\//) || "Invalid character!"],
});

export default {
	data: initData,

	watch: {
		visible(is, was) {
			// Reset all relevant data whenever dialog closes
			if (was && !is) this.reset();
		},
	},

	methods: {
		reset() {
			Object.assign(this, initData());
			this.$refs.errBoundary.resetError();
			this.$refs.form.resetValidation();
		},

		pop(item, handleSubmit) {
			this.node = item;
			this.name = item.name;
			this.handleSubmit = handleSubmit;
			this.visible = true;
			return new Promise(resolve => {
				this.resolve = resolve;
			});
		},

		async close(confirmed) {
			if (!confirmed) {
				this.visible = false;
				if (this.resolve) this.resolve(false);
				return;
			}

			const valid = this.$refs.form.validate();
			if (!valid) return;

			this.loading = true;

			const formPayload = {
				node: this.node,
				name: this.name,
			};
			try {
				let resp = formPayload;
				if (this.handleSubmit) {
					resp = await this.handleSubmit(formPayload);
				}
				if (this.resolve) {
					this.resolve(resp);
				}
				this.visible = false;
			} finally {
				this.loading = false;
			}
		},

		handleFormError(err) {
			// We only want to handle some errors. Keep propogating any others.
			const isApiErr =
				err instanceof APIError && !(err instanceof APIAuthError);
			const isNodeOpErr = err instanceof NodeOperationError;
			return !(isApiErr || isNodeOpErr);
		},
	},
};
</script>
